import React, { useEffect, useState } from "react";
import { Column } from 'primereact/column';
import CompanysDialog from "./dialog";
import CompanyAPI from "../../services/CompanyApi";
import IApiCompanyResponse from "../../interfaces/IApiCompanyResponse";
import { useTranslation } from 'react-i18next';
import { useToast } from "../../context/ToastContext";

// Design System
import PageHeader from "../../components/PageHeader/PageHeader";
import FilterPanel from "../../components/FilterPanel/FilterPanel";
import DataTable from "../../components/DataTable/DataTable";
import Input from "../../components/Input/Input";
import { Button } from "../../components/Button/Button";
import { IconButton } from "../../components/IconButton/IconButton";
import type { PaginatorPageChangeEvent } from "../../components/Paginator/Paginator";
import ConfirmModal from "../../components/ConfirmModal/ConfirmModal";

const RegisterCompany: React.FC = () => {
  const [companys, setcompanys] = useState<IApiCompanyResponse[]>([]);
  const [filteredEvents, setFilteredcompanys] = useState<IApiCompanyResponse[]>([]);

  const [inputValueCompanys, setInputValueCompanys] = useState<string | null>(null);
  const [inputValueDescricao, setInputValueDescricao] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedStation, setSelectedCompanys] = useState<IApiCompanyResponse | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [companyToDelete, setCompanyToDelete] = useState<string | null>(null);

  // Pagination/Sort state
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState(10);
  const [sortField, setSortField] = useState<string>("name");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  const { displayToast } = useToast();
  const { t: translateMessage } = useTranslation("messages");
  const { t: translateForm } = useTranslation("form");
  const { t: translateGeneral } = useTranslation("general");

  const fetchcompanys = async (): Promise<void> => {
    setIsLoading(true);
    try {
      const response = await CompanyAPI.GetAllCompanies();
      const fetchedcompanys = response.data || [];
      setcompanys(fetchedcompanys);
      setFilteredcompanys(fetchedcompanys);
    } catch (error) {
      console.error('Failed : ', error);
      displayToast(
          "error",
          translateMessage("errorSearch"),
          "",
          false,
          3000
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchcompanys();
  }, []);

  const createCompany = async (name: string, description: string): Promise<void> => {
    try {
        const response = await CompanyAPI.PostCompaniesCreate({ name, description });
        if (response.data) {
            fetchcompanys();
            displayToast(
              "success",
              translateMessage("registerSuccess"),
              "",
              false,
              3000
          );
        }
    } catch (error: any) {
        console.error('Failed: ', error.data);
        displayToast(
            "error",
            `${translateMessage("error")}: ${error.data.error}`,
            "",
            false,
            8000
        );

    }
  };
  const deleteStation = async (id: string): Promise<void> => {
    try {
        await CompanyAPI.DeleteCompanies(id);
        fetchcompanys();
        displayToast(
            "success",
            translateMessage("deleteSuccess"),
            "",
            false,
            3000
        );
    } catch (error) {
        console.error('Failed to delete station: ', error);
        displayToast(
            "error",
            `${translateMessage("error")}: ${error}`,
            "",
            false,
            3000
        );
    }
};
const updateStation = async (id: string, name: string, description: string): Promise<void> => {
  try {
    const response = await CompanyAPI.UpdateCompanies( { id, name, description });
    fetchcompanys();
    if (response.statusCode === 200) {
      fetchcompanys();
            displayToast(
              "success",
              translateMessage("editSuccess"),
              "",
              false,
              3000
          );
    }
  } catch (error) {
    console.error('Failed: ', error);
        displayToast(
            "error",
            `${translateMessage("error")}: ${error}`,
            "",
            false,
            3000
        );
  }
};

const handleEditClick = (station: IApiCompanyResponse): void => {
  setSelectedCompanys(station);
  setEditMode(true);
  setVisible(true);
};

const handleDialogSubmit = (name: string, description: string): void => {
  if (editMode && selectedStation) {
    updateStation(selectedStation.id, name, description);
  } else {
    createCompany(name, description);
  }
  setVisible(false);
  setSelectedCompanys(null);
  setEditMode(false);
};


  //#endregion

  //#region  Filters
  const resetFilters = (): void => {
    setInputValueCompanys(null);
    setInputValueDescricao(null);
    setFilteredcompanys(companys);
  };

  const filtercompanys = (): void => {
    let filtered = companys;

    if (inputValueCompanys) {
      filtered = filtered.filter((event: IApiCompanyResponse): boolean => {
        return event.name.toLowerCase().includes(inputValueCompanys.toLowerCase());
      });
    }

    if (inputValueDescricao) {
      filtered = filtered.filter((event: IApiCompanyResponse): boolean => {
        return event.description.trim().toLowerCase().includes(inputValueDescricao.trim().toLowerCase());
      });
    }

    setFilteredcompanys(filtered);
  };

  const alertDelete = (stationId: string): void => {
    setCompanyToDelete(stationId);
    setShowDeleteConfirm(true);
  };

  const handlePageChange = (e: PaginatorPageChangeEvent): void => {
    setPage(e.first);
    setRows(e.rows);
  };

  const handleSortChange = (e: any): void => {
    const dir = e.sortOrder === 1 ? "asc" : "desc";
    setSortField(e.sortField);
    setSortOrder(dir);
  };

  //#endregion

 //#region  Table View
  return (
    <div className="overflow-hidden">
      <PageHeader
        title={translateForm("company")}
        description="Gerencie as empresas cadastradas no sistema"
      >
        <Button
          onClick={() => {
            setSelectedCompanys(null);
            setEditMode(false);
            setVisible(true);
          }}
          label={translateGeneral("register")}
          icon="add"
          severity="primary"
        />
      </PageHeader>

      <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
        <FilterPanel
          title="Filtros"
          onApply={filtercompanys}
          onClear={resetFilters}
        >
          <Input
            type="text"
            label={translateForm("name")}
            value={inputValueCompanys || ""}
            onChange={(val: any) => setInputValueCompanys(val)}
          />
          <Input
            type="text"
            label={translateForm("description")}
            value={inputValueDescricao || ""}
            onChange={(val: any) => setInputValueDescricao(val)}
          />
        </FilterPanel>

        {isLoading ? (
          <div>{translateGeneral("loading")}</div>
        ) : (
          <DataTable
            value={filteredEvents}
            paginated
            first={page}
            rows={rows}
            totalRecords={filteredEvents.length}
            onPageChange={handlePageChange}
            rowsPerPageOptions={[10, 20, 50]}
            loading={isLoading}
            sortField={sortField}
            sortOrder={sortOrder === "asc" ? 1 : -1}
            onSort={handleSortChange}
            emptyMessage={translateGeneral("noRecordsFound")}
          >
            <Column
              field="name"
              header={translateForm("name")}
              sortable
            />
            <Column
              field="description"
              header={translateForm("description")}
              sortable
            />
            <Column
              field="actions"
              header={translateGeneral("actions")}
              body={(rowData: IApiCompanyResponse) => (
                <div style={{ display: "flex", gap: "0.5rem" }}>
                  <IconButton
                    icon="edit"
                    variant="text"
                    severity="secondary"
                    onClick={() => handleEditClick(rowData)}
                    aria-label={translateGeneral("edit")}
                  />
                  <IconButton
                    icon="delete"
                    variant="text"
                    severity="secondary"
                    onClick={() => alertDelete(rowData.id)}
                    aria-label={translateGeneral("delete")}
                  />
                </div>
              )}
              style={{ width: "8rem" }}
            />
          </DataTable>
        )}

        <CompanysDialog
          visible={visible}
          onHide={() => setVisible(false)}
          onCreate={handleDialogSubmit}
          companys={selectedStation}
        />

        <ConfirmModal
          visible={showDeleteConfirm}
          onHide={() => setShowDeleteConfirm(false)}
          onConfirm={() => {
            if (companyToDelete) deleteStation(companyToDelete);
            setShowDeleteConfirm(false);
          }}
          title={translateGeneral("confirmation")}
          message={translateMessage("deleteCompany")}
        />
      </div>
    </div>
  );
  //#endregion
};

export default RegisterCompany;
